import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import useLatest from "@-/com.npmjs.use-latest";
import { toast } from "@-/com.npmjs.sonner";
import pTimeout, { TimeoutError } from "@-/com.npmjs.p-timeout";
import { boundedPromise } from "@-/dev.improve.haniverse.lib.deadline";
import { safeRequestAnimationFrame } from "@-/dev.improve.haniverse.lib.scheduler";
import {
  runOnJS,
  runOnUI,
  useAnimatedReaction,
  withSpring,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";
import {
  type RatingLatchLease,
  releaseRatingLatch,
  tryAcquireRatingLatch,
} from "@-/dev.improve.haniverse.lib.cards.review-latch";
import {
  createLearnCardOperationId,
  createLearnCardSessionId,
  isLearnCardActionAllowedFor,
  isSameLearnCardAction,
  type LearnCardAction,
  type LearnCardActionReceipt,
  type LearnCardActionSnapshot,
  type LearnCardActionTrigger,
  type LearnCardExitDirection,
  learnCardActionForHorizontalSwipe,
  learnCardExitDirectionForAction,
  learnCardPresentationKey,
  LearnCardActionOutcomeUnknownError,
} from "@-/dev.improve.haniverse.lib.learn.card-action";
import {
  applyLearnCardControlsClaim,
  canFinishLearnCardExit,
  failedLearnCardOperationId,
  flashcardQueueExtension,
  flashcardQueueSuccessor,
  initialLearnDeckState,
  isFlashcardQueueEntryReady,
  LEARN_DECK_RECOVERY_SPRING,
  type LearnCardFace,
  type LearnCardViewMode,
  type LearnDeckEvent,
  type LearnDeckHistoryRestore,
  type LearnDeckState,
  type LearnCardControlsClaim,
  type LearnCardReleaseMode,
  type LearnDeckSurface,
  learnCardControlsForPresentation,
  learnDeckReducer,
  learnDeckSwipeHold,
} from "@-/dev.improve.haniverse.lib.learn.deck";
import type {
  LearnCardDestination,
  LearnCardDestinationResolution,
} from "@-/dev.improve.haniverse.lib.learn.destination";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { useQueryClient } from "@-/com.npmjs.tanstack__react-query";
import { preparePageNavigatorQueries } from "@-/dev.improve.haniverse.lib.navigator.page";
import type { LearnDeckNavigatorVariables } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.navigator";
import { useFlashcardQueuePlanInputs } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-action";
import { NavContext } from "@-/dev.improve.haniverse.routes.nav._context";
import { useLearnDeckOperations } from "#learn-deck-operations-context";
import type {
  LearnCardControls,
  LearnFlashcardExplorationDestination,
  LearnDeckContextValue,
  LearnDeckStageValues,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import {
  completeLearnCardRelease,
  LEARN_DECK_EXIT_DURATION_MS,
  LEARN_DECK_FLIP_DURATION_MS,
  LEARN_DECK_PARK_SPRING,
  learnCardExitTarget,
  learnCardParkPose,
  pageInLearnCardSuccessor,
  placeEnteringLearnCard,
  releaseLearnCard,
  resetLearnDeckMotion,
  resumeStalledLearnCardRelease,
  returnReleasedLearnCard,
  slideInLearnCard,
  useLearnDeckStageValues,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-gestures";
import { useOtherDeviceChangesPulledGeneration } from "@-/dev.improve.haniverse.lib.sync-push.signal";
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";

import { useLearnCardRecovery } from "#use-learn-card-recovery";
import { useLearnCardDragStart } from "#use-learn-card-drag-start";

import type { LearnDeckEntry } from "#learn-deck-entry";

type CardItem = LearnDeckState["current"]["item"];

/**
 * No committed action may show its spinner indefinitely. Local writes and reads settle in
 * milliseconds; these bounds only exist so that ANY wedge — a hung query, a surface that never
 * reports, a lost callback — becomes a state the learner can act on instead of an endless spinner.
 */
const LEARN_DECK_WRITE_DEADLINE_MS = 8000;
const LEARN_DECK_LOADING_DEADLINE_MS = 6000;
/** Silent fresh mounts of the next flashcard before the learner is told loading failed. */
const LEARN_DECK_LOADING_REBUILDS = 1;
/**
 * The pending button gets one frame on screen before the write starts. A frame is ~16ms; past
 * this the write proceeds unpainted rather than wait on frames a backgrounded app never delivers.
 */
const LEARN_DECK_PAINT_YIELD_DEADLINE_MS = 250;
/**
 * A swipe held for its card (see `learnDeckSwipeHold`) waits at most this long. Everything it
 * waits for is already bounded (the write, the loading deadline and its rebuild), so this is only
 * the backstop that guarantees the parked card comes back.
 */
const LEARN_DECK_HELD_SWIPE_DEADLINE_MS =
  LEARN_DECK_WRITE_DEADLINE_MS + LEARN_DECK_LOADING_DEADLINE_MS * (LEARN_DECK_LOADING_REBUILDS + 1);
/**
 * A card Undo brings back waits off the stage until it can draw what it shows. One still mounted
 * from its flight is ready at once; one mounted again reports within a few frames. Past this it
 * slides in regardless and draws its own loading state (the recovery hook rebuilds a card that
 * never reports), so Undo's spinner never outlives it.
 */
const LEARN_DECK_HISTORY_ENTRANCE_DEADLINE_MS = 3000;

function itemForEntry(entry: LearnDeckEntry, levelIndex: number): CardItem {
  if (entry.kind === "slide") {
    return { kind: "slide", slideSlug: entry.slideSlug };
  }
  if (entry.kind === "quiz") {
    return { kind: "quiz", quizSlug: entry.quizSlug, source: entry.source };
  }
  if (entry.kind === "lesson") {
    return {
      grapheme: entry.grapheme,
      kind: "lesson",
      levelIndex,
      ...(entry.part === undefined ? {} : { part: entry.part }),
      ...(entry.pinyin === undefined ? {} : { pinyin: entry.pinyin }),
    };
  }
  return {
    contextSentence: entry.contextSentence,
    drillType: entry.drillType,
    grapheme: entry.grapheme,
    kind: "review",
  };
}

/** The resolver origin for an item: reviews (any drill kind) key on the grapheme/word. */
function snapshotOriginForItem(item: CardItem): LearnCardActionSnapshot {
  if (item.kind === "slide") {
    return { kind: "slide", slideSlug: item.slideSlug };
  }
  if (item.kind === "quiz") {
    return { kind: "quiz", quizSlug: item.quizSlug, source: item.source };
  }
  return item.kind === "lesson"
    ? {
        grapheme: item.grapheme,
        kind: "lesson",
        levelIndex: item.levelIndex,
        ...(item.part === undefined ? {} : { part: item.part }),
        ...(item.pinyin === undefined ? {} : { pinyin: item.pinyin }),
      }
    : { card: null, drillType: item.drillType, grapheme: item.grapheme, kind: "review" };
}

/** The deck navigator variables that prepare a CARD destination; `null` for route destinations. */
function deckNavigatorVariablesForDestination(
  destination: LearnCardDestination,
): LearnDeckNavigatorVariables | null {
  switch (destination.kind) {
    case "slide": {
      return { kind: "slide", slideSlug: destination.lesson.slug };
    }
    case "quiz": {
      return { kind: "quiz", quizSlug: destination.quizSlug, source: destination.source };
    }
    case "review": {
      return {
        drillType: destination.card.drillType,
        grapheme: destination.card.grapheme,
        kind: "review",
      };
    }
    case "lesson": {
      return {
        grapheme: destination.lesson.graphemeCard.front,
        kind: "lesson",
        ...(destination.lesson.part === undefined ? {} : { part: destination.lesson.part }),
        ...(destination.lesson.pinyin === undefined ? {} : { pinyin: destination.lesson.pinyin }),
      };
    }
    default: {
      return null;
    }
  }
}

function learnDeckItemTraceLabel(item: CardItem): string {
  switch (item.kind) {
    case "slide": {
      return item.slideSlug;
    }
    case "quiz": {
      return item.quizSlug;
    }
    default: {
      return item.grapheme;
    }
  }
}

/** The reducer, traced: every event and the phase it lands in, for on-device transition debugging. */
function tracedLearnDeckReducer(state: LearnDeckState, event: LearnDeckEvent): LearnDeckState {
  const next = learnDeckReducer(state, event);
  logger(import.meta.location).debug("Learn deck event", {
    changed: next !== state,
    current: learnDeckItemTraceLabel(next.current.item),
    destinationStatus:
      next.transition.phase === "committed" ? next.transition.destinationStatus : null,
    event: event.type,
    face: next.face,
    phase: next.transition.phase,
    queue: next.queue.map((entry) =>
      entry.kind === "card"
        ? { key: entry.key, ready: entry.slot.ready }
        : { key: entry.key, ready: null },
    ),
    sessionId: next.sessionId,
    surface: event.type === "surface-ready" ? event.surface : null,
    view: next.view,
    writeStatus: next.transition.phase === "committed" ? next.transition.writeStatus : null,
  });
  return next;
}

/**
 * The deck's brain: the reducer plus every effect that connects it to the world — planning and
 * preloading the flashcard queue, the committed action's write, the exit and promotion, flips and
 * view switches — and the UI-thread stage values they animate.
 *
 * Ordering rules, and where they live:
 *   * The card under the current one is the card an action leads to → the flashcard queue
 *     (`state.queue`) is planned ahead, one flashcard at a time after its tail, and is the only
 *     thing preloaded and promoted. No write's outcome re-decides a queued flashcard.
 *   * A card cannot leave onto a flashcard that is not ready → a release stalls at the snap point
 *     until the next flashcard's front has rendered (`releaseLearnCard`), and
 *     `canFinishLearnCardExit` gates the logical exit on the write AND that readiness.
 *   * Readiness arriving while the finger is down never dismisses the card → the write only
 *     starts at `commit`, which the gesture issues on release.
 *   * Persistence and loading are independent → a loading retry mounts the same next flashcard
 *     again without touching the write; a write failure restores an actionable card without
 *     recording anything.
 *   * Stale callbacks cannot advance another card → every completion carries the operation id
 *     and the reducer drops mismatches; the shared rating latch is held from commit to promotion.
 */
export function useLearnDeckController({
  entry,
  historyRestore,
  isFocused = true,
  isHistoryPending = false,
  isLearnFocused = isFocused,
  levelIndex,
  onActionSaved,
  onCurrentChange,
  onRouteExit,
}: {
  entry: LearnDeckEntry;
  historyRestore?: LearnDeckHistoryRestore | null;
  isFocused?: boolean;
  isHistoryPending?: boolean;
  isLearnFocused?: boolean;
  levelIndex: number;
  onActionSaved?: (
    receipt: LearnCardActionReceipt,
    snapshot: LearnCardActionSnapshot,
    direction: LearnCardExitDirection,
  ) => void;
  /** Fired whenever the current card, face or view settles, so the route can mirror it. */
  onCurrentChange: (state: LearnDeckState) => void;
  onRouteExit?: (state: LearnDeckState, resolution: LearnCardDestinationResolution) => void;
}): LearnDeckContextValue & {
  contextValue: LearnDeckContextValue;
  horizontalDrag: {
    onCommit: (direction: "left" | "right", presentationKey: string, gestureId: number) => void;
    onDragCancel: (presentationKey: string) => void;
    onDragStart: (presentationKey: string, motionEpoch: number) => void;
    onPark: (direction: "left" | "right", presentationKey: string) => void;
  };
  /** Undo's card is waiting off the stage until it can draw what it shows. */
  isRestoringCard: boolean;
  onStageLayout: (size: { height: number; width: number }) => void;
  onSurfaceReady: (presentationKey: string, surface: LearnDeckSurface) => void;
  onSurfacesReleased: (presentationKey: string) => void;
} {
  const [state, dispatch] = useReducer(tracedLearnDeckReducer, undefined, (): LearnDeckState =>
    initialLearnDeckState({
      face: entry.face,
      item: itemForEntry(entry, levelIndex),
      sessionId: createLearnCardSessionId(),
      view: entry.view,
    }),
  );
  const stateRef = useLatest(state);
  const isFocusedRef = useLatest(isFocused);
  const historyPendingRef = useLatest(isHistoryPending);
  const actionSavedRef = useLatest(onActionSaved);
  const routeExitRef = useLatest(onRouteExit);
  const stage = useLearnDeckStageValues(learnCardPresentationKey(state.current.presentation));
  const motionEpochRef = useRef(0);
  const resetMotion = useCallback(
    (face: LearnCardFace, presentationKey: string) => {
      const epoch = ++motionEpochRef.current;
      runOnUI(() => {
        resetLearnDeckMotion(stage, face, presentationKey, epoch);
      })();
    },
    [stage],
  );
  // The current card's action did not go through: bring it back to rest, from its parked edge or
  // from wherever its release had flown it.
  const returnHome = useCallback(() => {
    const epoch = motionEpochRef.current;
    const { current, face } = stateRef.current;
    const presentationKey = learnCardPresentationKey(current.presentation);
    runOnUI(() => {
      if (stage.motionEpoch.get() !== epoch) {
        return;
      }
      if (returnReleasedLearnCard(stage, presentationKey, face)) {
        return;
      }
      if (stage.committedPresentation.get() === presentationKey) {
        stage.committedPresentation.set(null);
      }
      if (stage.isDragging.get() || stage.exitingOperation.get() !== null) {
        return;
      }
      stage.dragX.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
      stage.dragY.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
      stage.tilt.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
    })();
  }, [stage, stateRef]);
  // A swipe the deck will not commit (or no longer holds): the card goes back to rest unless a
  // finger, a newer commit or an exit has it.
  const returnRefusedSwipe = useCallback(
    (presentationKey: string) => {
      const { current, face } = stateRef.current;
      const restingFace =
        learnCardPresentationKey(current.presentation) === presentationKey ? face : "front";
      runOnUI(() => {
        if (returnReleasedLearnCard(stage, presentationKey, restingFace)) {
          return;
        }
        if (
          stage.motionPresentation.get().current !== presentationKey ||
          stage.committedPresentation.get() === presentationKey ||
          stage.isDragging.get() ||
          stage.exitingOperation.get() !== null
        ) {
          return;
        }
        stage.dragX.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
        stage.dragY.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
        stage.tilt.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
      })();
    },
    [stage, stateRef],
  );
  useEffect(() => {
    stage.flipProgress.set(entry.face === "back" ? 1 : 0);
  }, [entry.face, stage]);
  // Apply each history restore exactly once, keyed by the restore itself rather than by the motion
  // helper it calls: replaying it re-dispatched the restore and re-published the frame in a loop.
  //
  // Undo brings a card an action sent away back the way it left. It takes the motion where that
  // action left it, off the stage, while the card on show goes back beneath it, and slides in once
  // it can draw what it shows (`entrance`, below). Any other restore lands in place.
  const resetMotionRef = useLatest(resetMotion);
  const stageRef = useLatest(stage);
  const [entrance, setEntrance] = useState<{ epoch: number; presentationKey: string } | null>(null);
  const entranceRef = useLatest(entrance);
  useEffect(() => {
    if (historyRestore == null) {
      return;
    }
    const { enterFrom, state: restored } = historyRestore;
    const presentationKey = learnCardPresentationKey(restored.current.presentation);
    const shownKey = learnCardPresentationKey(stateRef.current.current.presentation);
    if (enterFrom === null || presentationKey === shownKey) {
      setEntrance(null);
      resetMotionRef.current(restored.face, presentationKey);
      dispatch({ state: restored, type: "restore-history" });
      return;
    }
    const event = { retainShown: true, state: restored, type: "restore-history" } as const;
    // Usually the card is still mounted from its flight and can draw at once: it slides in from
    // the same UI-thread call, with no render in between holding the deck's controls.
    const next = learnDeckReducer(stateRef.current, event);
    const isReady = next.current.ready[next.view === "info" ? "info" : next.face];
    const epoch = ++motionEpochRef.current;
    const stageValues = stageRef.current;
    runOnUI(() => {
      placeEnteringLearnCard(stageValues, {
        direction: enterFrom,
        epoch,
        face: restored.face,
        presentationKey,
        underlayKey: shownKey,
      });
      if (isReady) {
        slideInLearnCard(stageValues, presentationKey, epoch);
      }
    })();
    dispatch(event);
    setEntrance(isReady ? null : { epoch, presentationKey });
  }, [historyRestore, resetMotionRef, stageRef, stateRef]);
  const {
    arrive,
    controller,
    navigate,
    navigatorOptions: resolveDeckNavigatorOptions,
    prepare,
    resolve,
  } = useLearnDeckOperations();
  const queryClient = useQueryClient();
  // A card successor mounts UNDER the current card; strictly prepare everything its surfaces read
  // first so the mount never suspends behind the card it is about to replace.
  const prefetchCardDestination = useCallback(
    async (destination: LearnCardDestination) => {
      const variables = deckNavigatorVariablesForDestination(destination);
      if (variables === null) {
        return;
      }
      const { queries } = resolveDeckNavigatorOptions(variables);
      // These reads are independent. Strict fetching surfaces a failed query instead of
      // mounting a card with missing data and waiting forever for its readiness marker.
      await preparePageNavigatorQueries(queryClient, queries);
    },
    [queryClient, resolveDeckNavigatorOptions],
  );
  const [setHasLearnFlowStartedThisSession] = useContextSelectors(
    NavContext,
    (c) => c.setHasLearnFlowStartedThisSession,
  );
  // Keyed by the PRESENTATION that registered them, never a bare slot. Two cards are mounted
  // across a promotion and React does not order the outgoing cleanup against the incoming
  // effect, so an unkeyed cell lets a leaving card erase its successor's controls and strand
  // the deck (dead Reveal/rating buttons under a card that renders perfectly). Holding the
  // owner's key makes a stale write identifiable, so it can be dropped instead of applied.
  const [cardControlsClaim, setCardControlsClaim] =
    useState<LearnCardControlsClaim<LearnCardControls>>(null);
  const currentPresentationKey = learnCardPresentationKey(state.current.presentation);
  // Controls belonging to any other presentation are not this card's, so they are not shown.
  const cardControls = learnCardControlsForPresentation(cardControlsClaim, currentPresentationKey);
  const cardControlsRef = useLatest(cardControls);
  // An in-card exploration belongs to the presentation that opened it. Promotion, Undo or a new
  // session shows every other card from its own surfaces.
  const [explorationClaim, setExplorationClaim] = useState<{
    destination: LearnFlashcardExplorationDestination;
    presentationKey: string;
  } | null>(null);
  const exploration =
    explorationClaim?.presentationKey === currentPresentationKey
      ? explorationClaim.destination
      : null;
  useEffect(() => {
    setExplorationClaim((claim) =>
      claim === null || claim.presentationKey === currentPresentationKey ? claim : null,
    );
  }, [currentPresentationKey]);
  const onDragStart = useLearnCardDragStart({
    cardControls,
    dispatch,
    isFocused,
    motionEpochRef,
    state,
  });
  const [cardLayout, setCardLayout] = useState({ height: 0, width: 0 });
  const onCurrentChangeRef = useLatest(onCurrentChange);

  // A lesson swipe released before the reveal its own drag requested has landed (a quick flick
  // from the front) owes the learner the next reading once the card is on its back. Keyed by the
  // presentation, so it never carries over to another card; the free-tier gate can refuse the
  // reveal, in which case the card stays on its front and nothing advances.
  const pendingPronunciationAdvanceRef = useRef<string | null>(null);
  const viewNextLessonPronunciation = useCallback(() => {
    const controls = cardControlsRef.current;
    if (controls?.kind !== "lesson" || !controls.hasUnviewedPronunciation) {
      return;
    }
    if (stateRef.current.view === "info" && stateRef.current.transition.phase === "idle") {
      dispatch({ to: "study", type: "switch-view" });
      dispatch({ type: "switch-view-finished" });
    }
    controls.viewNextPronunciation();
  }, [cardControlsRef, stateRef]);
  useEffect(() => {
    const pending = pendingPronunciationAdvanceRef.current;
    if (pending === null) {
      return;
    }
    if (pending !== currentPresentationKey) {
      pendingPronunciationAdvanceRef.current = null;
      return;
    }
    if (state.face === "back" && cardControls?.kind === "lesson") {
      pendingPronunciationAdvanceRef.current = null;
      viewNextLessonPronunciation();
    }
  }, [cardControls, currentPresentationKey, state.face, viewNextLessonPronunciation]);

  // Surface readiness only drives drawing: no action waits for a face to report a layout (a
  // reveal before the back has rendered draws the back's loading state). The recovery hook still
  // rebuilds a card whose VISIBLE surface never reports, so a stuck card is repaired, not waited on.
  const visibleSurface = state.view === "info" ? "info" : state.face;
  const { getCardRenderKey } = useLearnCardRecovery({
    hasControls: cardControls !== null,
    isAtRest:
      !isHistoryPending &&
      entrance === null &&
      (state.transition.phase === "idle" || state.transition.phase === "dragging"),
    isCurrentCardReady: state.current.ready[visibleSurface],
    isCurrentCardRendered: state.current.ready.front,
    isFocused,
    presentationKey: currentPresentationKey,
  });

  // ---- History entrance: Undo's card slides in once it can draw what it shows ----
  // A card Undo brings back is usually still mounted from its flight (`previous`) and slides in with
  // the restore itself. One that mounts again waits off the stage, over the card on show, while Undo
  // shows its spinner; bounded, so a card that never reports still comes in and draws its loading
  // state.
  const entranceKey = entrance?.presentationKey ?? null;
  const entranceEpoch = entrance?.epoch ?? null;
  const isCurrentCardVisibleReady = state.current.ready[visibleSurface];
  useEffect(() => {
    if (entranceKey === null || entranceEpoch === null) {
      return;
    }
    const finish = (slideIn: boolean) => {
      if (slideIn) {
        const stageValues = stageRef.current;
        runOnUI(() => {
          slideInLearnCard(stageValues, entranceKey, entranceEpoch);
        })();
      }
      setEntrance((pending) => (pending?.epoch === entranceEpoch ? null : pending));
    };
    if (currentPresentationKey !== entranceKey) {
      // Another restore or a new session took the stage; its own motion owns it.
      finish(false);
      return;
    }
    if (isCurrentCardVisibleReady) {
      finish(true);
      return;
    }
    const timer = setTimeout(() => {
      logger(import.meta.location).warn("Learn deck: restored card never reported ready", {
        presentationKey: entranceKey,
      });
      finish(true);
    }, LEARN_DECK_HISTORY_ENTRANCE_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [currentPresentationKey, entranceEpoch, entranceKey, isCurrentCardVisibleReady, stageRef]);

  // The operation currently holding the shared rating latch, and its lease, released on promotion
  // or failure. Releasing by lease means an expired hold can never release the next rating's.
  const latchOperationRef = useRef<{ lease: RatingLatchLease; operationId: string } | null>(null);
  const releaseLatchFor = useCallback((operationId: string) => {
    const held = latchOperationRef.current;
    if (held?.operationId === operationId) {
      latchOperationRef.current = null;
      releaseRatingLatch(held.lease);
    }
  }, []);
  useEffect(
    () => () => {
      const held = latchOperationRef.current;
      if (held !== null) {
        latchOperationRef.current = null;
        releaseRatingLatch(held.lease);
      }
    },
    [],
  );

  // ---- UI-thread mirrors of the next flashcard ----
  const successor = flashcardQueueSuccessor(state);
  const isSuccessorReady = isFlashcardQueueEntryReady(successor);
  const successorPresentationKey =
    successor?.kind === "card" ? learnCardPresentationKey(successor.slot.presentation) : null;
  // What a committing release does with the current card (see `LearnCardReleaseMode`). It leaves at
  // once (or stalls at the snap point until the next flashcard is ready) unless the queue ends, so
  // the deck is about to be left, or the card is already committed; a lesson whose next reading is
  // unviewed turns to it instead of leaving, and a quiz not yet checked has no answer to commit, so
  // it springs home.
  const lessonAwaitsPronunciation =
    cardControls?.kind === "lesson" && cardControls.hasUnviewedPronunciation;
  const quizAwaitsCheck = cardControls?.kind === "quiz" && cardControls.answer === null;
  const releaseMode: LearnCardReleaseMode =
    lessonAwaitsPronunciation || quizAwaitsCheck
      ? "return"
      : successor?.kind === "end" || state.transition.phase === "committed"
        ? "park"
        : "fly";
  useEffect(() => {
    // Publish readiness and identity together in one UI task.
    runOnUI(() => {
      stage.isSuccessorReady.set(isSuccessorReady);
      stage.successorPresentation.set(successorPresentationKey);
      stage.releaseMode.set(releaseMode);
    })();
  }, [isSuccessorReady, releaseMode, stage, successorPresentationKey]);
  // A card released before the next flashcard was ready waits at the snap point; it flies off the
  // moment that flashcard has rendered its front, uncovering exactly the card that was preloaded.
  useAnimatedReaction(
    () => ({
      dragging: stage.isDragging.get(),
      exiting: stage.exitingOperation.get(),
      ready: stage.isSuccessorReady.get(),
      stalled: stage.stalledRelease.get(),
      successor: stage.successorPresentation.get(),
    }),
    ({ ready, stalled }) => {
      if (stalled === null || !ready) {
        return;
      }
      resumeStalledLearnCardRelease(stage);
    },
  );

  const onStageLayout = useCallback(
    ({ height, width }: { height: number; width: number }) => {
      setCardLayout((current) =>
        current.height === height && current.width === width ? current : { height, width },
      );
      stage.cardHeight.set(height);
      stage.cardWidth.set(width);
    },
    [stage],
  );

  const onSurfaceReady = useCallback(
    (presentationKey: string, surface: LearnDeckSurface) => {
      const current = stateRef.current;
      const event = { presentationKey, surface, type: "surface-ready" } as const;
      const isCurrent = presentationKey === learnCardPresentationKey(current.current.presentation);
      const successor = flashcardQueueSuccessor(current);
      const completesCommittedSwipe =
        current.transition.phase === "committed" &&
        successor?.kind === "card" &&
        presentationKey === learnCardPresentationKey(successor.slot.presentation);
      if (isCurrent || completesCommittedSwipe) {
        dispatch(event);
      } else {
        // Layout callbacks do not inherit the transition that mounted a speculative face.
        // Keep their follow-up deck render interruptible too; otherwise preparation switches
        // back to synchronous rendering while the current card is being studied.
        startTransition(() => dispatch(event));
      }
    },
    [stateRef],
  );
  const onSurfacesReleased = useCallback((presentationKey: string) => {
    dispatch({ presentationKey, type: "release-current-surfaces" });
  }, []);

  useEffect(() => {
    if (!isLearnFocused && state.transition.phase === "idle") {
      dispatch({ type: "trim-preloads" });
    }
  }, [isLearnFocused, state.queue, state.transition.phase]);

  // ---- Planning the flashcard queue ----
  // Everything the queue is planned from that changes outside the deck's own actions (a setting, the
  // review focus, the learner's preferences or tier, another device's study pulled in). The queue
  // already reflects `plannedKey`; when the inputs move on, its flashcards are planned again, but
  // only at rest: never while a card is being dragged, has been released or is committed, so the
  // card under it is never swapped while it can be seen or is about to be promoted.
  const planInputs = useFlashcardQueuePlanInputs();
  const remotePullGeneration = useOtherDeviceChangesPulledGeneration();
  const currentPlan = useMemo(
    () => ({ ...planInputs, remotePullGeneration }),
    [planInputs, remotePullGeneration],
  );
  const [plannedFor, setPlannedFor] = useState(currentPlan);
  const planKey = JSON.stringify(currentPlan);
  const plannedKey = JSON.stringify(plannedFor);
  const [replanTick, setReplanTick] = useState(0);
  const isReplanDue = planKey !== plannedKey && !isHistoryPending;
  useEffect(() => {
    if (!isReplanDue || state.transition.phase !== "idle") {
      return;
    }
    const isAtRest =
      !stage.isDragging.get() &&
      stage.release.get() === null &&
      stage.stalledRelease.get() === null &&
      stage.committedPresentation.get() === null &&
      stage.dragX.get() === 0 &&
      stage.dragY.get() === 0;
    if (!isAtRest) {
      // The card is still settling (a cancelled drag springing home): look again shortly.
      const timer = setTimeout(setReplanTick, 250, (tick: number) => tick + 1);
      return () => clearTimeout(timer);
    }
    setPlannedFor(currentPlan);
    dispatch({ type: "queue-invalidated" });
  }, [currentPlan, isReplanDue, replanTick, stage, state.transition.phase]);
  // Reaching the next flashcard may switch New Cards off itself (see `useLearnCardArrival`). The
  // queue was planned knowing that run was ending, so that change is already part of its plan.
  const arriveAt = useCallback(
    (kind: LearnCardDestination["kind"]) => {
      const { endedNewCardsRun } = arrive(kind);
      if (endedNewCardsRun) {
        setPlannedFor((planned) => ({ ...planned, reviewRechargeNewCardsEnabled: false }));
      }
    },
    [arrive],
  );

  // Extend the queue, one flashcard at a time, after its tail. The card under the current one is
  // planned as soon as the current card has drawn its front (or at once when the learner has already
  // acted on it); the one after it only while Learn is in front and the deck is resting, once the
  // card under the current one is ready, so lookahead never competes with what is on screen. A
  // resolution names the tail it follows and is dropped if the queue moved on; nothing it enqueues
  // ever replaces a queued flashcard.
  const [extensionAttempt, setExtensionAttempt] = useState(0);
  const extension = flashcardQueueExtension(state);
  const shouldExtend =
    extension !== null &&
    !isHistoryPending &&
    state.transition.phase !== "exiting" &&
    (state.queue.length === 0
      ? state.current.ready.front || state.transition.phase === "committed"
      : isLearnFocused && state.transition.phase === "idle" && isSuccessorReady);
  const extensionKey = shouldExtend
    ? `${extension.afterKey}|${plannedKey}|${extensionAttempt}`
    : null;
  useEffect(() => {
    if (extensionKey === null) {
      return;
    }
    const request = flashcardQueueExtension(stateRef.current);
    if (request === null) {
      return;
    }
    let cancelled = false;
    const { afterKey, origin, predecessors } = request;
    void resolve(controller.originFor(snapshotOriginForItem(origin)), {
      predictionOrigins: predecessors.map((item) =>
        controller.originFor(snapshotOriginForItem(item)),
      ),
    })
      .then(async (resolution) => {
        if (cancelled) {
          return;
        }
        await prefetchCardDestination(resolution.destination);
        if (cancelled) {
          return;
        }
        await prepare(resolution.destination);
        if (cancelled) {
          return;
        }
        // Speculative: a transition lets reveals, drags and ratings interrupt the mount it causes.
        // When the learner is already waiting on this flashcard, its spinner keeps turning and its
        // drag keeps tracking while it renders.
        startTransition(() => {
          dispatch({ afterKey, resolution, type: "flashcard-enqueued" });
        });
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        // A committed card waiting on it is bounded by the loading deadline below, which plans it
        // again from scratch.
        logger(import.meta.location).warn("Learn deck: flashcard queue planning failed", error);
      });
    return () => {
      cancelled = true;
    };
  }, [controller, extensionKey, prefetchCardDestination, prepare, resolve, stateRef]);

  // ---- Loading: a committed action whose write landed waits for the next flashcard ----
  const committedTransition = state.transition.phase === "committed" ? state.transition : null;
  // Every loading phase of a saved action is bounded, whatever holds it: planning or a query that
  // never settles, a flashcard surface that never reports its layout, or an exit that was allowed
  // but never started. The first deadline silently mounts the same next flashcard again (planning
  // it again if the queue was still empty); the second hands the card back to the learner with the
  // error toast and a working retry. A spinner never outlives both.
  const loadingOperation =
    committedTransition?.writeStatus === "saved" &&
    committedTransition.destinationStatus !== "failed"
      ? committedTransition.operationId
      : null;
  // The action button keeps its own label on failure; the toast is the only error surface. A
  // retry passes through `pending`, so a repeated failure of the same operation toasts again.
  const destinationFailedOperation =
    committedTransition?.destinationStatus === "failed" ? committedTransition.operationId : null;
  useEffect(() => {
    if (destinationFailedOperation === null) {
      return;
    }
    toast.error("Could not load the next card", {
      description: "Please tap the same button to try again.",
    });
  }, [destinationFailedOperation]);

  // ---- Failure: any failed write or loading returns the parked card to neutral ----
  // The error and its retry live in the action area. Leaving the card at the edge it was committed
  // toward reads as stuck mid-swipe; a retry parks it again (write) or exits from rest (loading).
  const failedOperation = failedLearnCardOperationId(state);
  useEffect(() => {
    if (failedOperation !== null) {
      returnHome();
    }
  }, [failedOperation, returnHome]);

  // ---- Exit: once the write landed and the next flashcard is ready ----
  // A card is promoted the moment the exit starts, and the rated card flies off above it as
  // `departing`; at the end of the queue the deck navigates once the card has flown off.
  const canExit = canFinishLearnCardExit(state);
  const finishedExitRef = useRef<string | null>(null);
  const finishExit = useCallback(
    (operationId: string) => {
      const current = stateRef.current;
      if (
        current.transition.phase !== "exiting" ||
        current.transition.operationId !== operationId ||
        finishedExitRef.current === operationId
      ) {
        return;
      }
      finishedExitRef.current = operationId;
      const successor = flashcardQueueSuccessor(current);
      if (successor?.kind === "end") {
        const { resolution } = successor;
        const epoch = motionEpochRef.current;
        arriveAt(resolution.destination.kind);
        // History observes a saved action; its failure must not prevent the prepared
        // navigation from starting or skip the exit's geometry and latch cleanup.
        try {
          routeExitRef.current?.(current, resolution);
        } catch (error) {
          logger(import.meta.location).error("Learn deck: route exit observer failed", error);
        }
        void navigate(resolution)
          .catch((error: unknown) => {
            logger(import.meta.location).error("Learn deck: navigation failed", error);
          })
          .finally(() => {
            if (motionEpochRef.current === epoch) {
              resetMotion(current.face, learnCardPresentationKey(current.current.presentation));
            }
            dispatch({ operationId, type: "exit-finished" });
            releaseLatchFor(operationId);
          });
        return;
      }
      // Transfer visual ownership and reset geometry in ONE UI worklet. The outgoing card is
      // retired before zero can reach it, regardless of when React commits the promotion.
      if (successor?.kind === "card") {
        resetMotion("front", learnCardPresentationKey(successor.slot.presentation));
      }
      dispatch({ operationId, type: "exit-finished" });
      releaseLatchFor(operationId);
    },
    [arriveAt, navigate, releaseLatchFor, resetMotion, routeExitRef, stateRef],
  );
  const finishExitRef = useLatest(finishExit);
  // The operation whose exit was started: a released card's exit is started without an exclusive
  // UI-thread claim, so a repeated request before React has rendered the promotion is dropped here.
  const startedExitRef = useRef<string | null>(null);
  const startExit = useCallback(
    (operationId: string) => {
      const current = stateRef.current;
      if (startedExitRef.current === operationId) {
        return;
      }
      if (
        !canFinishLearnCardExit(current) ||
        current.transition.phase !== "committed" ||
        current.transition.operationId !== operationId
      ) {
        runOnUI(() => {
          if (stage.exitingOperation.get() === operationId) {
            stage.exitingOperation.set(null);
            stage.exitProgress.set(0);
            stage.dragX.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
            stage.dragY.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
            stage.tilt.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
          }
        })();
        return;
      }
      const successor = flashcardQueueSuccessor(current);
      const { transition } = current;
      if (successor?.kind === "card" && transition.phase === "committed") {
        // Promote as the exit starts, so the next card is live at once. In ONE UI worklet, the
        // card keeps its pose as the departure and flies off along its own progress, while the
        // successor takes the shared motion at rest (see `learnCardMotionStyle`). Nothing waits
        // for the flight; the reducer drops the departing card on the same clock.
        const departingKey = learnCardPresentationKey(current.current.presentation);
        const successorKey = learnCardPresentationKey(successor.slot.presentation);
        const { direction } = transition;
        const epoch = ++motionEpochRef.current;
        startedExitRef.current = operationId;
        runOnUI(() => {
          // Released: it is already flying, and its successor already on show (and maybe being
          // dragged) keeps the motion.
          if (completeLearnCardRelease(stage, departingKey, successorKey, epoch)) {
            return;
          }
          const pose = { tilt: stage.tilt.get(), x: stage.dragX.get(), y: stage.dragY.get() };
          const target = learnCardExitTarget(stage, departingKey, direction);
          resetLearnDeckMotion(stage, "front", successorKey, epoch);
          stage.departure.set({
            presentation: departingKey,
            tilt: pose.tilt,
            toX: target.x,
            toY: target.y,
            x: pose.x,
            y: pose.y,
          });
          stage.departureProgress.set(withTiming(1, { duration: LEARN_DECK_EXIT_DURATION_MS }));
          pageInLearnCardSuccessor(stage, departingKey, pose.x);
        })();
        dispatch({ operationId, type: "exit-started" });
        releaseLatchFor(operationId);
        arriveAt(successor.slot.item.kind);
        return;
      }
      startedExitRef.current = operationId;
      dispatch({ operationId, type: "exit-started" });
    },
    [arriveAt, releaseLatchFor, stage, stateRef],
  );
  // The card promoted past is only drawn; once its flight's duration has passed it lands as the
  // deck's `previous` card, mounted for Undo and hidden rather than parked at the flight's end.
  const departingOperation = state.departing?.operationId ?? null;
  const departingKey =
    state.departing === null ? null : learnCardPresentationKey(state.departing.slot.presentation);
  useEffect(() => {
    if (departingOperation === null || departingKey === null) {
      return;
    }
    const timer = setTimeout(() => {
      dispatch({ operationId: departingOperation, type: "departure-finished" });
      const stageValues = stageRef.current;
      runOnUI(() => {
        if (stageValues.departure.get()?.presentation === departingKey) {
          stageValues.departure.set(null);
          stageValues.departureProgress.set(0);
        }
      })();
    }, LEARN_DECK_EXIT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [departingKey, departingOperation, stageRef]);
  // The loading deadline (see `loadingOperation`). Re-armed for each silent rebuild.
  const [loadingRebuild, setLoadingRebuild] = useState<{
    count: number;
    operationId: string;
  } | null>(null);
  const loadingRebuildCount =
    loadingRebuild?.operationId === loadingOperation ? loadingRebuild.count : 0;
  useEffect(() => {
    if (loadingOperation === null) {
      return;
    }
    const timer = setTimeout(() => {
      const current = stateRef.current;
      if (
        current.transition.phase !== "committed" ||
        current.transition.operationId !== loadingOperation
      ) {
        return;
      }
      if (canFinishLearnCardExit(current)) {
        // Everything is ready, yet the UI-thread exit never claimed the card (a stale claim or a
        // drag that never reported its end). Claim it and start the exit from JS; the exit
        // effect's own timer completes the promotion even if the animation is skipped.
        logger(import.meta.location).error("Learn deck: ready exit never started", {
          operationId: loadingOperation,
        });
        runOnUI(() => {
          if (stage.release.get() === null) {
            stage.exitingOperation.set(loadingOperation);
          }
        })();
        startedExitRef.current = null;
        startExit(loadingOperation);
        return;
      }
      logger(import.meta.location).error("Learn deck: loading timed out", {
        current: current.current.item,
        operationId: loadingOperation,
        queue: current.queue,
        rebuilds: loadingRebuildCount,
      });
      setExtensionAttempt((attempt) => attempt + 1);
      dispatch({ operationId: loadingOperation, type: "destination-failed" });
      if (loadingRebuildCount < LEARN_DECK_LOADING_REBUILDS) {
        // Batched with the failure above, so the learner never sees the failed state: mount the
        // same next flashcard again (or plan it again, if none was queued yet).
        dispatch({ operationId: loadingOperation, type: "retry-destination" });
        setLoadingRebuild({ count: loadingRebuildCount + 1, operationId: loadingOperation });
      }
    }, LEARN_DECK_LOADING_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [loadingOperation, loadingRebuildCount, stage, startExit, stateRef]);

  const exitOperation =
    canExit && state.transition.phase === "committed" ? state.transition.operationId : null;
  const exitDirection =
    state.transition.phase === "committed" || state.transition.phase === "exiting"
      ? state.transition.direction
      : "up";
  const motionEpoch = motionEpochRef.current;
  useAnimatedReaction(
    () => ({
      dragging: stage.isDragging.get(),
      operationId: exitOperation,
      released: stage.release.get()?.from === currentPresentationKey,
    }),
    ({ dragging, operationId, released }) => {
      if (
        operationId === null ||
        stage.exitingOperation.get() !== null ||
        stage.motionEpoch.get() !== motionEpoch
      ) {
        return;
      }
      if (released) {
        // The card left at its release, and any finger down now is on the card shown in its
        // place: nothing to acquire, and nothing to wait for.
        runOnJS(startExit)(operationId);
        return;
      }
      if (dragging) {
        return;
      }
      // Acquire ownership before asking React to clear the loading button. A delayed JS
      // drag-start cannot steal a held card; a new touch cannot cancel an acquired exit.
      stage.exitingOperation.set(operationId);
      runOnJS(startExit)(operationId);
    },
  );
  const exitingOperation =
    state.transition.phase === "exiting" ? state.transition.operationId : null;
  useEffect(() => {
    if (exitingOperation === null) {
      return;
    }
    // Only the end of the queue reaches `exiting` (a card is promoted as its exit starts; see
    // `startExit`): the deck is left once this card is gone. React has committed the exiting
    // phase, removing the loading label/spinner. Only now release the parked card into its
    // dismissal animation, keeping the two states in order.
    runOnUI(() => {
      if (
        stage.exitingOperation.get() !== exitingOperation ||
        stage.motionEpoch.get() !== motionEpoch
      ) {
        return;
      }
      stage.stalledRelease.set(null);
      const target = learnCardExitTarget(stage, currentPresentationKey, exitDirection);
      const timing = { duration: LEARN_DECK_EXIT_DURATION_MS };
      stage.exitProgress.set(withTiming(1, timing));
      stage.dragX.set(withTiming(target.x, timing));
      stage.dragY.set(withTiming(target.y, timing));
    })();
    // Logical completion has its own lifetime. Animation cancellation/reduced motion cannot
    // swallow a callback and leave the deck permanently exiting.
    const timer = setTimeout(
      () => finishExitRef.current(exitingOperation),
      LEARN_DECK_EXIT_DURATION_MS,
    );
    return () => clearTimeout(timer);
  }, [currentPresentationKey, exitDirection, exitingOperation, finishExitRef, motionEpoch, stage]);

  // ---- Commit ----
  const actionSnapshotRef = useRef<{
    operationId: string;
    snapshot: LearnCardActionSnapshot;
  } | null>(null);
  // The history observer hears each saved operation once, although a timed-out write and its
  // retry can both land.
  const notifiedSavedOperationRef = useRef<string | null>(null);
  const settleWrite = useCallback(
    (
      operationId: string,
      epoch: number,
      receipt: LearnCardActionReceipt,
      snapshot: LearnCardActionSnapshot,
      direction: LearnCardExitDirection,
    ) => {
      if (receipt.persisted.kind === "enrollment" && !receipt.persisted.wasAdded) {
        // Past the free tier: the gate has presented the paywall; the card stays.
        dispatch({ operationId, type: "write-blocked" });
        if (motionEpochRef.current === epoch) {
          returnHome();
        }
        releaseLatchFor(operationId);
        return;
      }
      dispatch({ operationId, receipt, type: "write-saved" });
      if (stateRef.current.current.item.kind === "review") {
        // Reaching here means the launch-only Learn summary greeting has been consumed.
        setHasLearnFlowStartedThisSession(true);
      }
      if (notifiedSavedOperationRef.current === operationId) {
        return;
      }
      notifiedSavedOperationRef.current = operationId;
      // A history observer cannot turn a successful write into a replayable write failure.
      try {
        actionSavedRef.current?.(receipt, snapshot, direction);
      } catch (error) {
        logger(import.meta.location).error("Learn deck: saved action observer failed", error);
      }
    },
    [actionSavedRef, releaseLatchFor, returnHome, setHasLearnFlowStartedThisSession, stateRef],
  );
  const commitWithDirection = useCallback(
    async (
      action: LearnCardAction,
      trigger: LearnCardActionTrigger,
      direction: LearnCardExitDirection,
    ) => {
      const current = stateRef.current;
      const { phase } = current.transition;
      const retry =
        current.transition.phase === "committed" && current.transition.writeStatus === "unknown"
          ? current.transition
          : null;
      if (historyPendingRef.current || entranceRef.current !== null) {
        return false;
      }
      if (!isFocusedRef.current) {
        return false;
      }
      if (phase !== "idle" && phase !== "dragging" && retry === null) {
        return false;
      }
      if (retry !== null) {
        action = retry.action;
        direction = retry.direction;
        trigger = retry.trigger;
      }
      if (!isLearnCardActionAllowedFor(current.current.item.kind, action)) {
        return false;
      }
      const controls = cardControlsRef.current;
      if (controls === null) {
        return false;
      }
      const latch = tryAcquireRatingLatch();
      if (latch === null) {
        return false;
      }
      const operationId = retry?.operationId ?? createLearnCardOperationId();
      const epoch = motionEpochRef.current;
      latchOperationRef.current = { lease: latch, operationId };
      dispatch(
        retry === null
          ? { action, direction, operationId, trigger, type: "commit" }
          : { type: "recover" },
      );
      // The card leaves on the press, before any of the work below can occupy the JS thread: it
      // flies off at once, uncovering the next flashcard (or stalls at the snap point until that
      // flashcard is ready), unless the queue ends, in which case it parks at its waiting offset
      // until its exit can start. A swipe already did this on the UI thread at its release.
      const presentationKey = learnCardPresentationKey(current.current.presentation);
      runOnUI(() => {
        if (
          stage.motionEpoch.get() !== epoch ||
          stage.isDragging.get() ||
          stage.exitingOperation.get() !== null ||
          stage.motionPresentation.get().current !== presentationKey ||
          stage.committedPresentation.get() === presentationKey
        ) {
          return;
        }
        if (stage.releaseMode.get() !== "park" && stage.release.get() === null) {
          releaseLearnCard(stage, presentationKey, direction);
          return;
        }
        stage.committedPresentation.set(presentationKey);
        // An `up` exit waits square and untilted: leaning it left or right would betray which
        // rating is being written, and the card is about to travel straight up regardless.
        const parked = learnCardParkPose(stage, presentationKey, direction);
        stage.dragX.set(withSpring(parked.x, LEARN_DECK_PARK_SPRING));
        stage.dragY.set(withSpring(parked.y, LEARN_DECK_PARK_SPRING));
        stage.tilt.set(withSpring(parked.tilt, LEARN_DECK_PARK_SPRING));
      })();
      let write: Promise<{
        receipt: LearnCardActionReceipt;
        snapshot: LearnCardActionSnapshot;
      }> | null = null;
      try {
        // Publish the pending button before checkpoint/snapshot work can occupy the JS thread.
        // A resolved promise only yields to microtasks, and an animation-frame callback runs
        // before paint; resume in the following task so the spinner gets a frame on screen.
        // The rating latch above already prevents another press from submitting this action.
        // A hold, so it fails open: a backgrounded app pauses animation frames, and the write
        // must not wait on a paint that is not coming.
        // A slide skips it: its content and successor come from the bundled catalogue and its
        // completion is written behind, so there is no wait to show and the card leaves at once.
        if (current.current.item.kind !== "slide") {
          await boundedPromise(
            {
              deadline: LEARN_DECK_PAINT_YIELD_DEADLINE_MS,
              onDeadline: "resolve",
              operation: "Learn deck pending-button paint",
            },
            (resolve) => {
              let timer: ReturnType<typeof setTimeout> | undefined;
              const cancelFrame = safeRequestAnimationFrame(() => {
                timer = setTimeout(resolve, 0);
              }, "learn-deck-pending-button-paint");
              return () => {
                cancelFrame();
                clearTimeout(timer);
              };
            },
          );
        }

        // A fast reveal + swipe can share one React batch. Preserve the answer checkpoint before
        // committing, so Back always returns to the answer that received this rating.
        onCurrentChangeRef.current({
          ...current,
          // A slide or a quiz has one face.
          face:
            current.current.item.kind === "slide" || current.current.item.kind === "quiz"
              ? "front"
              : "back",
          transition: { phase: "idle" },
        });

        // A failed acknowledgement can follow a committed write. Retain both the row IDs and
        // frozen sentence/card snapshot so retrying cannot rate a second time or different cards.
        write = (async () => {
          const previousSnapshot = actionSnapshotRef.current;
          const snapshot =
            previousSnapshot !== null && previousSnapshot.operationId === operationId
              ? previousSnapshot.snapshot
              : await controls.getActionSnapshot();
          actionSnapshotRef.current = { operationId, snapshot };
          const receipt = await controller.persist({ action, operationId, snapshot, trigger });
          return { receipt, snapshot };
        })();
        const { receipt, snapshot } = await pTimeout(write, {
          milliseconds: LEARN_DECK_WRITE_DEADLINE_MS,
        });
        settleWrite(operationId, epoch, receipt, snapshot, direction);
      } catch (error) {
        logger(import.meta.location).error("Learn deck: action write failed", error);
        // Only a write whose outcome is unknown pins the card to retrying itself. Any other
        // failure rolled back, so the card returns to rest with every action available again.
        // A write past its deadline may still land, so it is unknown too: the card offers the
        // exactly-once retry of the same operation instead of spinning.
        const isTimedOut = error instanceof TimeoutError;
        const isOutcomeUnknown = error instanceof LearnCardActionOutcomeUnknownError || isTimedOut;
        if (isTimedOut && write !== null) {
          // A late landing still advances the card (the retry, if pressed, finds its rows and is a
          // no-op); a late failure leaves the retry in place.
          void write.then(
            ({ receipt, snapshot }) =>
              settleWrite(operationId, epoch, receipt, snapshot, direction),
            (lateError: unknown) => {
              logger(import.meta.location).warn("Learn deck: timed-out write failed", lateError);
            },
          );
        }
        dispatch({
          operationId,
          type: isOutcomeUnknown ? "write-outcome-unknown" : "write-failed",
        });
        releaseLatchFor(operationId);
        if (motionEpochRef.current === epoch) {
          if (!isOutcomeUnknown) {
            returnHome();
          }
          toast.error(
            action.kind === "rate" || action.kind === "answer-quiz"
              ? "Could not save the review"
              : "Could not save progress",
            {
              description: isOutcomeUnknown
                ? "Please tap the same button to try again."
                : "Please try again.",
            },
          );
        }
      }
      return true;
    },
    [
      cardControlsRef,
      controller,
      entranceRef,
      historyPendingRef,
      isFocusedRef,
      onCurrentChangeRef,
      releaseLatchFor,
      settleWrite,
      stage,
      returnHome,
      stateRef,
    ],
  );

  const commit = useCallback(
    (action: LearnCardAction, trigger: LearnCardActionTrigger) => {
      void commitWithDirection(action, trigger, learnCardExitDirectionForAction(action));
    },
    [commitWithDirection],
  );

  // A released swipe is never dropped for a reason that is about to pass. The card on show may not
  // be React's current card yet (it was uncovered by a release whose exit has not started), or may
  // still be registering its controls, or finishing a flip: its commit is held and replayed the
  // moment the deck can take it (see the effect below). Only a swipe the deck would refuse anyway
  // goes back to rest.
  const [heldSwipe, setHeldSwipe] = useState<{
    direction: "left" | "right";
    gestureId: number;
    presentationKey: string;
  } | null>(null);
  const horizontalDrag = useMemo(
    () => ({
      onCommit: async (direction: "left" | "right", presentationKey: string, gestureId: number) => {
        const deck = stateRef.current;
        const hold = learnDeckSwipeHold(deck, presentationKey, cardControlsRef.current !== null);
        if (hold === "hold") {
          setHeldSwipe({ direction, gestureId, presentationKey });
          return;
        }
        if (hold === "refuse") {
          returnRefusedSwipe(presentationKey);
          return;
        }
        // A checked quiz commits the answer it was graded with, whichever way it is swiped; an
        // unchecked one has nothing to commit and springs home (its release mode is `return`).
        const quizControls = cardControlsRef.current;
        const action =
          stateRef.current.current.item.kind === "quiz"
            ? quizControls?.kind === "quiz" && quizControls.answer !== null
              ? { correct: quizControls.answer.correct, kind: "answer-quiz" as const }
              : null
            : learnCardActionForHorizontalSwipe(stateRef.current.current.item.kind, direction);
        // A lesson is accepted only once the learner has viewed every reading it introduces. Until
        // then a swipe springs back and does what "Next Pronunciation" does (bring Learn forward and
        // open the next unviewed reading) — also when the same drag started on the front and
        // revealed the card: a learner may hold the drag to read the back, so the release means
        // "show me the next view", not "stay on the meaning".
        const controls = cardControlsRef.current;
        const isLessonAwaitingPronunciation =
          action?.kind === "accept-lesson" &&
          controls?.kind === "lesson" &&
          controls.hasUnviewedPronunciation;
        if (isLessonAwaitingPronunciation) {
          if (stateRef.current.face === "back") {
            viewNextLessonPronunciation();
          } else {
            pendingPronunciationAdvanceRef.current = presentationKey;
          }
        }
        // A write whose outcome is unknown can only be retried as itself (its rows may already have
        // landed), so a swipe toward a different action springs back instead of replaying it.
        const { transition } = stateRef.current;
        const retriesUnknownWrite =
          transition.phase !== "committed" ||
          transition.writeStatus !== "unknown" ||
          (action !== null && isSameLearnCardAction(transition.action, action));
        const accepted =
          action !== null &&
          !isLessonAwaitingPronunciation &&
          retriesUnknownWrite &&
          (await commitWithDirection(action, "swipe", direction));
        if (!accepted) {
          dispatch({ type: "drag-cancel" });
          returnRefusedSwipe(presentationKey);
        }
      },
      onDragCancel: (presentationKey: string) => {
        if (learnCardPresentationKey(stateRef.current.current.presentation) !== presentationKey) {
          return;
        }
        dispatch({ type: "drag-cancel" });
      },
      onDragStart,
      onPark: (direction: "left" | "right", presentationKey: string) => {
        if (learnCardPresentationKey(stateRef.current.current.presentation) !== presentationKey) {
          return;
        }
        dispatch({ direction, type: "park-drag" });
      },
    }),
    [
      cardControlsRef,
      commitWithDirection,
      onDragStart,
      returnRefusedSwipe,
      stateRef,
      viewNextLessonPronunciation,
    ],
  );
  // Replay a held swipe once its card can take it; give it back once it never will.
  const heldSwipeKey = heldSwipe?.presentationKey ?? null;
  const heldSwipeHold =
    heldSwipeKey === null ? null : learnDeckSwipeHold(state, heldSwipeKey, cardControls !== null);
  useEffect(() => {
    if (heldSwipe === null || heldSwipeHold === "hold") {
      return;
    }
    setHeldSwipe(null);
    if (heldSwipeHold === "refuse" || !isFocused) {
      returnRefusedSwipe(heldSwipe.presentationKey);
      return;
    }
    // A slide with views left turns its view, as a swipe on its top view would have.
    const pages = cardControls?.kind === "slide" ? cardControls.pages : null;
    if (pages !== null && pages.current < pages.count - 1) {
      returnRefusedSwipe(heldSwipe.presentationKey);
      pages.goTo(pages.current + 1);
      return;
    }
    void horizontalDrag.onCommit(
      heldSwipe.direction,
      heldSwipe.presentationKey,
      heldSwipe.gestureId,
    );
  }, [cardControls, heldSwipe, heldSwipeHold, horizontalDrag, isFocused, returnRefusedSwipe]);
  // Bounded: every wait it depends on is bounded too, but a hold must end even if one is not.
  useEffect(() => {
    if (heldSwipe === null) {
      return;
    }
    const timer = setTimeout(() => {
      setHeldSwipe((held) => (held === heldSwipe ? null : held));
      returnRefusedSwipe(heldSwipe.presentationKey);
    }, LEARN_DECK_HELD_SWIPE_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [heldSwipe, returnRefusedSwipe]);

  const recover = useCallback(() => {
    const { transition } = stateRef.current;
    if (transition.phase === "committed" && transition.writeStatus === "unknown") {
      void commitWithDirection(transition.action, transition.trigger, transition.direction);
    }
  }, [commitWithDirection, stateRef]);

  // Set while a saved action is being rolled back: loading its successor now would advance past a
  // review that is about to disappear.
  const undoingSavedOperationRef = useRef<string | null>(null);
  const retryDestination = useCallback(() => {
    const { transition } = stateRef.current;
    if (transition.phase === "committed" && undoingSavedOperationRef.current === null) {
      setExtensionAttempt((attempt) => attempt + 1);
      dispatch({ operationId: transition.operationId, type: "retry-destination" });
    }
  }, [stateRef]);

  // ---- Undo a saved action whose successor failed to load ----
  // The write landed but the card could not leave, so the action is not in history yet and
  // history's Undo has nothing to step back to. Rolling the write back here returns the card to
  // rest, where every action is available again.
  const [undoingSavedOperation, setUndoingSavedOperation] = useState<string | null>(null);
  const undoableSavedOperation =
    committedTransition?.writeStatus === "saved" &&
    committedTransition.destinationStatus === "failed"
      ? committedTransition.operationId
      : null;
  const undoSavedAction = useCallback(() => {
    const { transition } = stateRef.current;
    if (
      transition.phase !== "committed" ||
      transition.writeStatus !== "saved" ||
      transition.destinationStatus !== "failed" ||
      transition.receipt === null ||
      undoingSavedOperationRef.current !== null
    ) {
      return;
    }
    const { operationId, receipt } = transition;
    undoingSavedOperationRef.current = operationId;
    setUndoingSavedOperation(operationId);
    void controller
      .undo(receipt)
      .then(() => {
        actionSnapshotRef.current = null;
        dispatch({ operationId, type: "saved-action-undone" });
        releaseLatchFor(operationId);
      })
      .catch((error: unknown) => {
        logger(import.meta.location).error("Learn deck: undoing a saved action failed", error);
        toast.error("Could not undo the review", { description: "Please try again." });
      })
      .finally(() => {
        undoingSavedOperationRef.current = null;
        setUndoingSavedOperation(null);
      });
  }, [controller, releaseLatchFor, stateRef]);
  const savedActionUndo = useMemo(
    () =>
      undoableSavedOperation === null
        ? null
        : {
            isPending: undoingSavedOperation === undoableSavedOperation,
            undo: undoSavedAction,
          },
    [undoSavedAction, undoableSavedOperation, undoingSavedOperation],
  );

  // ---- Flip ----
  const flip = useCallback(
    (to?: LearnCardFace, animation?: "swipe-up") => {
      const current = stateRef.current;
      const isDragging = current.transition.phase === "dragging";
      if ((!isDragging && current.transition.phase !== "idle") || current.view !== "study") {
        return;
      }
      const target = to ?? (current.face === "front" ? "back" : "front");
      if (target === current.face) {
        return;
      }
      if (isDragging) {
        if (target === "back") {
          stage.flipProgress.set(1);
          dispatch({ to: "back", type: "flip" });
        }
        return;
      }
      dispatch({ to: target, type: "flip" });
      // Only an intentional upward swipe animates. In particular, a horizontal drag's
      // auto-reveal must never wait for or inherit a flip, even after that drag has ended.
      if (animation !== "swipe-up" || current.current.item.kind !== "review") {
        stage.flipProgress.set(target === "back" ? 1 : 0);
        dispatch({ type: "flip-finished" });
        return;
      }
      stage.flipProgress.set(
        withTiming(target === "back" ? 1 : 0, { duration: LEARN_DECK_FLIP_DURATION_MS }),
      );
    },
    [stage, stateRef],
  );
  const flippingPresentation =
    state.transition.phase === "flipping"
      ? learnCardPresentationKey(state.current.presentation)
      : null;
  useEffect(() => {
    if (flippingPresentation === null) {
      return;
    }
    const timer = setTimeout(() => {
      const current = stateRef.current;
      if (learnCardPresentationKey(current.current.presentation) === flippingPresentation) {
        dispatch({ type: "flip-finished" });
      }
    }, LEARN_DECK_FLIP_DURATION_MS);
    return () => clearTimeout(timer);
  }, [flippingPresentation, stateRef]);

  // ---- View switch ----
  // Instant: the binder tabs show the other view at once. Info mounts on first use and keeps
  // its state on later switches; while its data loads it shows its own placeholder.
  // Info opens from the ANSWER only.
  const switchView = useCallback(
    (to: LearnCardViewMode) => {
      const current = stateRef.current;
      if (current.transition.phase !== "idle" || current.view === to) {
        return;
      }
      if (to === "info" && current.face !== "back") {
        return;
      }
      dispatch({ to, type: "switch-view" });
      dispatch({ type: "switch-view-finished" });
    },
    [stateRef],
  );

  // ---- In-card exploration ----
  // A link prepared asynchronously is dropped once its card was swiped away or blurred.
  const explore = useCallback(
    (presentationKey: string, destination: LearnFlashcardExplorationDestination) => {
      const current = stateRef.current;
      if (
        isFocusedRef.current &&
        learnCardPresentationKey(current.current.presentation) === presentationKey &&
        (current.transition.phase === "idle" || current.transition.phase === "dragging")
      ) {
        setExplorationClaim({ destination, presentationKey });
      }
    },
    [isFocusedRef, stateRef],
  );
  const closeExploration = useCallback(() => {
    setExplorationClaim(null);
    switchView("study");
  }, [switchView]);

  // ---- Mirror settled state to the caller (route params) ----
  useEffect(() => {
    if (state.transition.phase === "idle" || state.transition.phase === "dragging") {
      onCurrentChangeRef.current(state);
    }
    // Readiness is part of the historical frame, so returning to it preserves its underlay.
    // The caller updates snapshots without rendering for readiness-only changes.
    // A preloaded inactive card has no history frame yet. Re-publish on activation even
    // when its ready state is unchanged, so its first reveal can return to the front.
  }, [isFocused, onCurrentChangeRef, state]);

  // Registration is a CLAIM by one presentation, and release only ever retracts that same
  // claim. A card leaving can therefore never clear the card that replaced it, in either
  // effect order, so the action area cannot be stranded without controls.
  const registerCardControls = useCallback(
    (presentationKey: string, controls: LearnCardControls | null) => {
      setCardControlsClaim((claim) =>
        applyLearnCardControlsClaim(claim, presentationKey, controls),
      );
    },
    [],
  );

  const isTransitioning =
    isHistoryPending ||
    entrance !== null ||
    (state.transition.phase !== "idle" && state.transition.phase !== "dragging");
  // History restores a slide's presentation, not its mounted card: the view it was on lives here.
  const [slideViewMemory] = useState(() => new Map<string, number>());

  const value = useMemo<LearnDeckContextValue>(
    () => ({
      cardControls,
      cardLayout,
      closeExploration,
      commit,
      exploration,
      explore,
      flip,
      getCardRenderKey,
      isTransitioning,
      recover,
      registerCardControls,
      retryDestination,
      savedActionUndo,
      slideViewMemory,
      stage,
      state,
      switchView,
    }),
    [
      cardControls,
      cardLayout,
      closeExploration,
      getCardRenderKey,
      commit,
      exploration,
      explore,
      flip,
      isTransitioning,
      recover,
      registerCardControls,
      retryDestination,
      savedActionUndo,
      slideViewMemory,
      stage,
      state,
      switchView,
    ],
  );

  return {
    ...value,
    contextValue: value,
    horizontalDrag,
    isRestoringCard: entrance !== null,
    onStageLayout,
    onSurfaceReady,
    onSurfacesReleased,
  };
}

export type { LearnDeckStageValues, LearnDeckEvent };
